import React, { useState } from "react";
import { api } from "@/lib/api";
import { usePaywallGuard } from "@/lib/usePaywallGuard";
import { makeKey, getCache, setCache } from "@/lib/offlineCache";
import OfflineBadge from "@/components/OfflineBadge";
import DatePicker from "@/components/DatePicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollText, Check, X } from "lucide-react";

export default function NameNumerology() {
  const guard = usePaywallGuard();
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [system, setSystem] = useState("pythagorean");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cachedAt, setCachedAt] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setResult(null);
    setCachedAt(null);
    const body = { name, system };
    if (dob) body.dob = dob;
    const cacheKey = makeKey("name-numerology", body);
    const cached = getCache(cacheKey);
    if (cached) { setResult(cached.data); setCachedAt(cached.cachedAt); }
    try {
      const data = await guard(
        async () => (await api.post("/numerology/name", body)).data,
        "Name Numerology"
      );
      setResult(data);
      setCachedAt(null);
      setCache(cacheKey, data);
    } catch (err) {
      if (!cached) setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="name-numerology-page">
      <header>
        <h1 className="font-serif text-4xl text-slate-50 mb-2">Name Numerology Calculator</h1>
        <p className="text-slate-400">
          Discover your Destiny, Soul Urge, and Personality numbers — and check if your name vibrates with your birth chart.
        </p>
      </header>

      <Card className="glass border-white/10 p-6">
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="name-num-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Full Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="name-input"
                className="mt-1.5 bg-slate-900/50 border-white/10 text-white"
                placeholder="e.g., Rahul Sharma"
              />
            </div>
            <div>
              <Label className="text-slate-300">Date of Birth (optional, for compatibility)</Label>
              <div className="mt-1.5">
                <DatePicker
                  value={dob}
                  onChange={setDob}
                  testId="name-dob-input"
                  placeholder="Optional — for compatibility"
                />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-slate-300 mb-2 block">System</Label>
            <Tabs value={system} onValueChange={setSystem} data-testid="system-tabs">
              <TabsList className="bg-slate-900/50">
                <TabsTrigger value="pythagorean" data-testid="tab-pythagorean">Pythagorean</TabsTrigger>
                <TabsTrigger value="chaldean" data-testid="tab-chaldean">Chaldean</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="name-num-submit"
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium px-8"
          >
            {loading ? "Calculating..." : "Calculate"}
          </Button>
          {error && <div className="text-sm text-rose-400" data-testid="name-num-error">{error}</div>}
        </form>
      </Card>

      {result && (
        <div className="space-y-5 animate-fade-up" data-testid="name-num-result">
          {cachedAt && (
            <div className="flex justify-end">
              <OfflineBadge cachedAt={cachedAt} testId="name-num-offline-badge" />
            </div>
          )}
          <Card className="glass border-white/10 p-6">
            <div className="flex items-center gap-2 mb-4">
              <ScrollText className="w-5 h-5 text-gold" />
              <h3 className="font-serif text-2xl text-slate-50">{result.name}</h3>
              <span className="text-xs uppercase ml-auto tracking-wider text-amber-300 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20">
                {result.system}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Aspect label="Destiny Number" sub="What you're meant to do" data={result.destiny} />
              <Aspect label="Soul Urge" sub="Your inner desires" data={result.soul_urge} />
              <Aspect label="Personality" sub="How others see you" data={result.personality} />
            </div>
          </Card>

          {result.compatibility && (
            <Card className="glass border-white/10 p-6" data-testid="compatibility-card">
              <h3 className="font-serif text-2xl text-slate-50 mb-4">Name + Birth Compatibility</h3>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <Stat label="Birth Number" val={result.compatibility.birth_number} />
                <Stat label="Life Path" val={result.compatibility.life_path} />
                <Stat label="Name Destiny" val={result.compatibility.name_destiny} />
              </div>
              <div className={`flex items-start gap-3 p-4 rounded-xl ${result.compatibility.compatible ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-rose-500/10 border border-rose-500/20"}`}>
                {result.compatibility.compatible ? (
                  <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <X className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <div className={`font-medium ${result.compatibility.compatible ? "text-emerald-300" : "text-rose-300"}`}>
                    {result.compatibility.compatible ? "Compatible Vibration" : "Improvement Possible"}
                  </div>
                  <p className="text-sm text-slate-300 mt-1">{result.compatibility.advice}</p>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function Aspect({ label, sub, data }) {
  return (
    <div className="p-5 rounded-xl bg-slate-900/40 border border-white/5">
      <div className="text-xs uppercase tracking-wider text-amber-300">{label}</div>
      <div className="text-xs text-slate-500 mb-2">{sub}</div>
      <div className="font-serif text-5xl text-gold leading-none mb-3">{data.number}</div>
      <div className="text-slate-100 font-medium text-sm">{data.meaning.title}</div>
      <div className="text-xs text-slate-400 mt-1">{data.meaning.traits}</div>
    </div>
  );
}

function Stat({ label, val }) {
  return (
    <div className="text-center p-3 rounded-lg bg-slate-900/40">
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      <div className="text-3xl font-serif text-gold">{val}</div>
    </div>
  );
}
